/**
 * Monaco Collaboration Manager
 *
 * Draws remote collaborators in a Monaco editor: a 2 px caret in the person's colour, their selection tinted with a
 * solid underline, and a name flag above the caret (shown on move for a few seconds, then shrunk to the initials; the
 * full name stays in the flag's title). Colours come from the person's presence slot (utils/participantColors.js),
 * the same in the header, the file tree and the kanban.
 */

const FLAG_FULL_MS = 3000;

class MonacoCollaborationManager {
  constructor(editor, monaco) {
    this.editor = editor;
    this.monaco = monaco;

    this.remoteCursors = new Map(); // userId -> { color, username, position, lastUpdate }
    this.decorationIds = new Map(); // userId -> decoration ids
    this.widgetIds = new Map(); // userId -> content widget
    this.flagTimers = new Map(); // userId -> timer shrinking the flag to the initials
  }

  /** A CSS-safe class suffix for a user id. */
  static classFor(userId) {
    return String(userId).replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  /**
   * Update or create a remote cursor.
   * @param {string} userId - Remote user ID
   * @param {string} username - Display name
   * @param {string} color - Ink (hex)
   * @param {object} position - `{ line, column, selection?: { startLine, startColumn, endLine, endColumn }, typing? }`
   * @param {object} [extra] - `{ tint, initials }`
   */
  updateCursor(userId, username, color, position, extra = {}) {
    if (!position || typeof position.line !== 'number' || typeof position.column !== 'number') {
      return;
    }
    const previous = this.remoteCursors.get(userId);
    const moved = !previous || previous.position.line !== position.line || previous.position.column !== position.column;
    this.removeCursor(userId, { keepFlagTimer: true });

    const { line, column, selection } = position;
    const cls = MonacoCollaborationManager.classFor(userId);
    const decorations = [{
      range: new this.monaco.Range(line, column, line, column),
      options: {
        className: 'remote-cursor',
        stickiness: this.monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        beforeContentClassName: `remote-caret remote-caret-${cls}`,
      },
    }];
    if (selection && (selection.startLine !== selection.endLine || selection.startColumn !== selection.endColumn)) {
      decorations.push({
        range: new this.monaco.Range(selection.startLine, selection.startColumn, selection.endLine, selection.endColumn),
        options: {
          className: `remote-selection remote-selection-${cls}`,
          stickiness: this.monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
        },
      });
    }
    this.decorationIds.set(userId, this.editor.deltaDecorations([], decorations));
    this.injectCursorStyles(userId, color, extra.tint || `${color}33`);

    const full = moved || !previous?.compact;
    const widget = this.createCursorWidget(userId, username, color, line, column, { initials: extra.initials, compact: !full });
    this.widgetIds.set(userId, widget);
    this.remoteCursors.set(userId, { color, username, position, compact: !full, initials: extra.initials, lastUpdate: Date.now() });

    if (full) {
      clearTimeout(this.flagTimers.get(userId));
      this.flagTimers.set(userId, setTimeout(() => {
        const data = this.remoteCursors.get(userId);
        const node = this.widgetIds.get(userId)?.getDomNode();
        if (data && node && data.initials) {
          data.compact = true;
          node.textContent = data.initials;
          node.classList.add('is-compact');
        }
      }, FLAG_FULL_MS));
    }
  }

  /** The name flag above the caret: the full name for a while, then the initials (the title keeps the name). */
  createCursorWidget(userId, username, color, line, column, { initials, compact } = {}) {
    const domNode = document.createElement('div');
    domNode.className = `remote-cursor-widget${compact ? ' is-compact' : ''}`;
    domNode.style.backgroundColor = color;
    domNode.style.color = this.getContrastColor(color);
    domNode.textContent = compact && initials ? initials : username;
    domNode.title = username;
    domNode.setAttribute('data-user-id', userId);

    const widget = {
      domNode,
      getId: () => `remote-cursor-widget-${userId}`,
      getDomNode: () => domNode,
      getPosition: () => ({
        position: { lineNumber: line, column },
        preference: [
          this.monaco.editor.ContentWidgetPositionPreference.ABOVE,
          this.monaco.editor.ContentWidgetPositionPreference.BELOW,
        ],
      }),
    };

    this.editor.addContentWidget(widget);
    return widget;
  }

  removeCursor(userId, { keepFlagTimer = false } = {}) {
    const decorationIds = this.decorationIds.get(userId);
    if (decorationIds) {
      this.editor.deltaDecorations(decorationIds, []);
      this.decorationIds.delete(userId);
    }
    const widget = this.widgetIds.get(userId);
    if (widget) {
      this.editor.removeContentWidget(widget);
      this.widgetIds.delete(userId);
    }
    if (!keepFlagTimer) {
      clearTimeout(this.flagTimers.get(userId));
      this.flagTimers.delete(userId);
      this.remoteCursors.delete(userId);
      this.removeInjectedStyles(userId);
    }
  }

  clearAllCursors() {
    for (const userId of [...this.remoteCursors.keys()]) {
      this.removeCursor(userId);
    }
  }

  /** The person's caret and selection colours, as classes the decorations use. */
  injectCursorStyles(userId, color, tint) {
    const cls = MonacoCollaborationManager.classFor(userId);
    const styleId = `remote-cursor-style-${cls}`;
    const css = `
      .remote-caret-${cls}::before { content: ''; position: absolute; width: 2px; height: 1.2em; background-color: ${color}; margin-left: -1px; }
      .remote-selection-${cls} { background-color: ${tint}; border-bottom: 2px solid ${color}; }
    `;
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    if (style.textContent !== css) {
      style.textContent = css;
    }
  }

  removeInjectedStyles(userId) {
    document.getElementById(`remote-cursor-style-${MonacoCollaborationManager.classFor(userId)}`)?.remove();
  }

  /** White on dark inks, black on light ones. */
  getContrastColor(hexColor) {
    const hex = String(hexColor || '').replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  getActiveCursors() {
    return Array.from(this.remoteCursors.entries()).map(([userId, data]) => ({ userId, ...data }));
  }

  /** Removes cursors not updated for `maxAge` ms. */
  cleanupStaleCursors(maxAge = 30000) {
    const now = Date.now();
    for (const [userId, data] of this.remoteCursors.entries()) {
      if (now - data.lastUpdate > maxAge) {
        this.removeCursor(userId);
      }
    }
  }

  dispose() {
    this.clearAllCursors();
    this.decorationIds.clear();
    this.widgetIds.clear();
    this.remoteCursors.clear();
  }
}

export default MonacoCollaborationManager;
